
import AVIframe from '@/components/AVIframe.vue';
import WLDomainCard from '@/components/our-rates/WLDomainCard.vue';
import Store from '@/store';
import { minutesToHumanDuration } from '@/utils/dateHelpers';
import { IDomain, IProduct, getPartnerDetailsResult } from '@homeserve/od-typings/api';
import { domainHelper, FetchTreePayload, productHelper, treeHelper } from '@homeserve/vue-api-plugin';
import { WLStickySupportPhone } from '@homeserve/vue-components-plugin/src/components/WLStickySupportPhone';
import { breadcrumbHelper, diagnosticHelper } from '@homeserve/vue-components-plugin/src/main';
import GTMPageViewingMixin from '@homeserve/vue-components-plugin/src/mixins/GTMPageViewingMixin';
import { formatPhone } from '@homeserve/vue-components-plugin/src/utils/phoneHelper';
import { Component, Mixins } from 'vue-property-decorator';
import { Getter, State } from 'vuex-class';

@Component({
  components: {
    AVIframe,
    WLDomainCard,
    WLStickySupportPhone,
  },
})
export default class OurRatesByDomain extends Mixins(GTMPageViewingMixin) {
  private headers: any;
  private loading = false;
  private i18n: any;

  @Getter('getDomains', { namespace: 'api/catalog/domain' })
  private domainList!: IDomain[];

  @State('domain', { namespace: 'app/diagnostic' })
  private domainSlug!: string;

  @Getter('partner')
  private partner!: getPartnerDetailsResult;

  @State('allProductsPrices', { namespace: 'api/catalog/product' })
  private products!: IProduct[];

  get gtmRouterInfo() {
    return {
      domain: this.$t('transversal_domain'),
    };
  }

  get domainName() {
    const domain = this.domainList.find(d => d.slug === this.domainSlug);
    return domain?.name?.toLowerCase();
  }

  public getDuration(duration) {
    return minutesToHumanDuration(duration);
  }

  get supportPhone(): string {
    return formatPhone(this.partner.supportPhone, '0', true);
  }

  get domainProducts() {
    return this.products.filter(product => product.domain?.slug === this.domainSlug);
  }

  public async beforeRouteEnter(to, from, next) {
    const treePayload: FetchTreePayload = { domain: to.params.domain };
    await initContainer(to);
    await treeHelper.resetTree(Store);
    await treeHelper.fetchTree(Store, treePayload);
    if (Store.getters['api/tree/errorFetchingTree']) {
      return next('/404');
    }

    next();
  }

  public async beforeRouteUpdate(to, from, next) {
    await initContainer(to);
    next();
  }

  private i18nValue(i18nSlug) {
    return this.$t(i18nSlug) !== i18nSlug ? this.$t(i18nSlug) : null;
  }

  private async created() {
    this.i18n = {
      title: this.$t(`our_rates_by_domain.${this.domainSlug}.title`),
      desc: this.$t(`our_rates_by_domain.${this.domainSlug}.desc`),
      domain_card: {
        title: this.$t('our_rates.domain_card.title', {
          domain: this.domainName,
        }),
        button: this.$t('our_rates_by_domain.domain_card.button'),
      },
      price_title: this.$t(`our_rates_by_domain.${this.domainSlug}.price_title`),
      price_description: this.i18nValue(`our_rates_by_domain.${this.domainSlug}.price_description`),
      no_data: this.$t('our_rates.no_data'),
      packages_depending_on_slot: this.$t('our_rates.packages_depending_on_slot'),
      package_slot_normal_rate: this.$t('our_rates.package_slot_normal_rate'),
      package_slot_evening_saturday: this.$t('our_rates.package_slot_evening_saturday'),
      package_slot_sunday: this.$t('our_rates.package_slot_sunday'),
      whats_included: this.$t('our_rates.whats_included'),
      price_vat_displayed: this.$t('our_rates.price_vat_displayed'),
      table_headers: {
        job: this.$t('our_rates.table_headers.job'),
        base_price: this.$t('our_rates.table_headers.base_price'),
        evening_saturday_price: this.$t('our_rates.table_headers.evening_saturday_price'),
        sunday_price: this.$t('our_rates.table_headers.sunday_price'),
        duration: this.$t('our_rates.table_headers.duration'),
        spare_parts: this.$t('our_rates.table_headers.spare_parts'),
      },
    };
    this.headers = [
      {
        text: this.i18n.table_headers.job,
        value: '',
        sortable: false,
      },
      { text: this.i18n.table_headers.base_price, value: '', sortable: false },
      {
        text: this.i18n.table_headers.evening_saturday_price,
        value: '',
        sortable: false,
      },
      {
        text: this.i18n.table_headers.sunday_price,
        value: '',
        sortable: false,
      },
      {
        text: this.i18n.table_headers.duration,
        value: '',
        sortable: false,
        width: '10%',
      },
      { text: this.i18n.table_headers.spare_parts, value: '', sortable: false },
    ];
    this.loading = true;
    await domainHelper.fetchDomains(Store);
    await productHelper.fetchAllProductsPrices(Store);
    this.loading = false;
  }

  private async beforeUpdate() {
    this.i18n.title = this.$t(`our_rates_by_domain.${this.domainSlug}.title`);
    this.i18n.desc = this.$t(`our_rates_by_domain.${this.domainSlug}.desc`);
    this.i18n.domain_card.title = this.$t('our_rates.domain_card.title', {
      domain: this.domainName,
    });
    this.i18n.price_title = this.$t(`our_rates_by_domain.${this.domainSlug}.price_title`);
    this.i18n.price_description = this.i18nValue(`our_rates_by_domain.${this.domainSlug}.price_description`);
  }
}
async function initContainer(to) {
  const domain = to.params.domain;
  await diagnosticHelper.setDomain(Store, domain);
  await breadcrumbHelper.setDomain(Store, domain);
  await breadcrumbHelper.push(Store, {
    name: 'Tarifs dépannage',
    link: '/nos-tarifs',
  });
}
